import { HistoryCache } from "@web/route-services/chat/history-cache";
import { useRef } from "react";

interface Props {
  cache: HistoryCache;
}

type Generation = {
  kind: "generation";
  messageID: number;
  content: string;
};

type Regeneration = {
  kind: "regeneration";
  messageID: number;
  candidateID: number;
  content: string;
};

type RenderParams = Generation | Regeneration;

/**
 * How fast should chunks be rendered, higher is faster.
 */
const speed = 500;

/**
 * Hook to render text generation chunks as they come in. Rendering speed is configurable
 */
export const useRenderTextChunk = ({ cache }: Props) => {
  /**
   * We store the render params *once* in a ref so we don't have to store it as metadata in each chunk.
   */
  const renderParams = useRef<RenderParams>();
  /**
   * Render queue
   */
  const qRef = useRef<string[]>([]);

  const isRendering = useRef(false);

  const processChunk = () => {
    const fn = () => {
      const q = qRef.current;
      const content = q.shift();
      if (!content) return;
      const params = renderParams.current;
      if (!params) return;

      if (params.kind === "generation") {
        const target = cache.getByID(params.messageID);
        if (!target || !Array.isArray(target)) return;
        const newText = target[0].text + content;
        target[0].text = newText;
        cache.replace(params.messageID, target);
      }

      if (params.kind === "regeneration") {
        const target = cache.getByID(params.messageID);
        if (!target || !Array.isArray(target)) return;
        const idx = target.findIndex((m) => m.id === params.candidateID && m.kind === "candidate");
        if (!idx) return;
        target[idx].text = target[idx].text + content;
        cache.replace(params.messageID, target);
      }

      setTimeout(processChunk, Math.max(1, 1000 / speed));
      return true;
    };

    const res = fn();
    if (!res) isRendering.current = false;
  };

  return {
    renderTextChunk: (params: RenderParams) => {
      qRef.current.push(params.content);
      renderParams.current = params;
      if (!isRendering.current) {
        isRendering.current = true;
        processChunk();
      }
    }
  };
};
